import type { WorkspaceResponseV2 } from '@emma/api_client/types';
import { Box, Flex } from '@lego/klik-ui';
import { useState } from 'react';
import { AppWizardBoard, AppWizardServices } from '@/components/app_wizard';
import { Loader } from '@/components/common';
import { type RepositoryResponseDetailed, useRepositories } from '@/hooks/repository';
import { type StoreService, useStore } from '@/hooks/store';

export type DockerComposeObject = {
  version?: string;
  services?: Record<
    string,
    {
      [key: string]: unknown;
    }
  >;
};

type Props = {
  workspace: WorkspaceResponseV2;
};

export const WorkspaceAppWizardSubPage = (props: Props) => {
  const [selectedServices, setSelectedServices] = useState<
    (RepositoryResponseDetailed | StoreService)[]
  >([]);
  const [dockerComposeObject, setDockerComposeObject] = useState<DockerComposeObject>({
    version: '3.9',
  });

  const { data: storeData, isLoading: isStoreLoading, error: storeError } = useStore();
  const {
    data: workspaceData,
    isLoading: isWorkspaceLoading,
    error: workspaceError,
  } = useRepositories(props.workspace.id);

  const dataReady =
    !isStoreLoading &&
    !storeError &&
    storeData &&
    workspaceData &&
    !workspaceError &&
    !isWorkspaceLoading;

  const { repositories = [] } = workspaceData || {};
  const services = storeData?.services;

  const handleSelectedServicesChange = (
    newSelectedService: RepositoryResponseDetailed | StoreService
  ) => {
    const newSelectedServices = selectedServices.some(
      (service) =>
        service.imageName === newSelectedService.imageName &&
        service.tags.map((t) => t.tag).join(',') ===
          newSelectedService.tags.map((t) => t.tag).join(',')
    )
      ? selectedServices.filter((value) => value.imageName !== newSelectedService.imageName)
      : [...selectedServices, newSelectedService];

    setSelectedServices(newSelectedServices);
    setDockerComposeObject({
      version: '3.9',
      services: Object.fromEntries(
        newSelectedServices.map((service) => [
          service.name,
          {
            image: `${service.imageName}:${service.tags.map((t) => t.tag).join(',')}`,
          },
        ])
      ),
    });
  };

  const handleSelectChange = (serviceIdentifier: string | number) => {
    if (
      selectedServices.some(
        (service: StoreService | RepositoryResponseDetailed) =>
          ('imageName' in service && service.imageName === serviceIdentifier) ||
          ('id' in service && service.id === serviceIdentifier)
      )
    ) {
      const newSelectedServices = selectedServices.filter(
        (value) => value.imageName !== serviceIdentifier
      );
      setSelectedServices(newSelectedServices);
      if (newSelectedServices.length === 0) {
        setDockerComposeObject({
          version: '3.9',
        });
      } else {
        setDockerComposeObject({
          version: '3.9',
          services: Object.fromEntries(
            newSelectedServices.map((service) => [
              service.name,
              {
                image: `${service.imageName}:${service.tags.map((t) => t.tag).join(',')}`,
              },
            ])
          ),
        });
      }
    }
  };

  if (!dataReady) return <Loader />;

  return (
    <Box>
      <Flex direction={{ base: 'column', lg: 'row' }} gap={6} marginY={8}>
        <Flex direction="column" gap={6}>
          <AppWizardServices
            onSelectedServicesChange={handleSelectedServicesChange}
            onUnselectChange={handleSelectChange}
            services={repositories}
            title="Workspace Services"
            type="WorkspaceServices"
            workspaceId={props.workspace.id}
          />
          <AppWizardServices
            onSelectedServicesChange={handleSelectedServicesChange}
            onUnselectChange={handleSelectChange}
            services={services}
            title="EMMA Store Services"
            type="StoreServices"
          />
        </Flex>
        <AppWizardBoard dockerComposeObject={dockerComposeObject} />
      </Flex>
    </Box>
  );
};
